/**
 * CR229: Data Source Banner
 * 
 * Shows environment and data source info at the top of Admin UI.
 * Displays warnings if not in expected prod environment.
 */

import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Database, Globe, Server } from "lucide-react";

interface DataSourceBannerProps {
  environment?: "production" | "staging" | "development";
  supabaseProjectRef?: string;
  activeSnapshotId: string | null;
  adminSnapshotId: string | null;
  isLiveMode?: boolean;
}

export function DataSourceBanner({
  environment = "production",
  supabaseProjectRef,
  activeSnapshotId,
  adminSnapshotId,
  isLiveMode = false,
}: DataSourceBannerProps) {
  // Determine sync status
  const isSynced = activeSnapshotId === adminSnapshotId;
  const hasActiveSnapshot = !!activeSnapshotId;
  
  // Environment detection (can be enhanced with actual env detection)
  const envLabel = environment.charAt(0).toUpperCase() + environment.slice(1);
  const isNotProd = environment !== "production";

  return (
    <div className={`flex items-center justify-between px-4 py-2 rounded-lg border ${
      isNotProd 
        ? "bg-amber-500/10 border-amber-500/30" 
        : "bg-muted/50 border-border"
    }`}>
      <div className="flex items-center gap-3">
        {/* Environment Badge */}
        <Badge 
          variant="outline" 
          className={isNotProd ? "bg-amber-500/20 text-amber-700 border-amber-500/30" : ""}
        >
          <Server className="h-3 w-3 mr-1" />
          {envLabel}
        </Badge>

        {/* Data Source */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Database className="h-3 w-3" />
          <span>
            {isLiveMode 
              ? "cr075_production_deals (live filtered)"
              : "cr075_production_deals (admin filtered)"
            }
          </span>
        </div>

        {/* Project Ref (truncated) */}
        {supabaseProjectRef && (
          <div className="text-xs font-mono text-muted-foreground">
            ref: {supabaseProjectRef.slice(0, 8)}...
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Sync Status */}
        {hasActiveSnapshot ? (
          isSynced ? (
            <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/30 text-xs">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Admin = Live
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30 text-xs">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Admin ≠ Live
            </Badge>
          )
        ) : (
          <Badge variant="outline" className="bg-red-500/10 text-red-700 border-red-500/30 text-xs">
            <AlertTriangle className="h-3 w-3 mr-1" />
            No Live Snapshot
          </Badge>
        )}

        {/* Warning for non-prod */}
        {isNotProd && (
          <Badge variant="outline" className="bg-amber-500/20 text-amber-700 border-amber-500/30 text-xs">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Not Production
          </Badge>
        )}
      </div>
    </div>
  );
}
